import { useRef, useState } from 'react';
import { Button, Popover } from '@material-ui/core';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import PropTypes from 'prop-types';
import TooltipThumb from '@/component/common/TooltipThumb';
import ErrorHelper from '@/component/common/ErrorHelper';
import S3PathUtils from '@/common/utils/S3PathUtil';
import { uploadFileToS3 } from '@/service/s3Service';
import { validateHoldAudioFile } from '../handler/ProjectProfileManagementHandler';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

function HoldAudioFileUpload({
    projectId,
    holdMusicFileS3Key,
    holdMusicTmpS3Key,
    setHoldMusicTmpS3Key,
    holdMusicRemoved,
    setHoldMusicRemoved,
    setIsEdited,
    error,
    setError,
    disabled
}) {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const fileInputRef = useRef(null);

    const currentFileS3Key = holdMusicTmpS3Key || holdMusicFileS3Key;
    const hasFile = !!currentFileS3Key && !holdMusicRemoved;
    const fileName = hasFile ? S3PathUtils.getOriginalHoldMusicFileName(currentFileS3Key) : '';

    const handleFileSelect = async (file) => {
        if (!file) return;

        const { valid, errorKey } = validateHoldAudioFile(file);
        if (!valid) {
            setError(prev => ({ ...prev, holdAudioFile: trI18n(errorKey) }));
            return;
        }

        // Clear validation error when a valid file is selected
        setError(prev => ({ ...prev, holdAudioFile: '' }));

        // If already has file, show confirm dialog
        if (hasFile) {
            setPendingFile(file);
            setShowReplaceConfirm(true);
            return;
        }

        await uploadFile(file);
    };

    const uploadFile = async (file) => {
        const tmpPath = S3PathUtils.getTmpHoldMusicPath(projectId, file.name);
        const s3Key = tmpPath.replace('public/', '');
        const result = await uploadFileToS3(s3Key, { file, key: s3Key });

        if (!result) return;

        setHoldMusicTmpS3Key(tmpPath);
        setHoldMusicRemoved(false);
        setIsEdited(true);
    };

    const handleConfirmReplace = async () => {
        setShowReplaceConfirm(false);
        if (pendingFile) {
            await uploadFile(pendingFile);
            setPendingFile(null);
        }
    };

    const handleCancelReplace = () => {
        setShowReplaceConfirm(false);
        setPendingFile(null);
    };

    const handleRemoveFile = () => {
        setMenuAnchorEl(null);
        setHoldMusicRemoved(true);
        setHoldMusicTmpS3Key('');
        setIsEdited(true);
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
        e.target.value = '';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    return (
        <>
            <div className="project-email-setting-group email-setting-function">
                <div className="title-name">{trI18n('HOLD_AUDIO_FILE')}</div>
            </div>
            <div className="project-email-setting-group email-setting-function">
                <div className="hold-music-content">
                    {hasFile && (
                        <div className="lstName-file" style={{ marginBottom: 10 }}>
                            <ul style={{ display: 'flex', columnGap: 10, rowGap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                <li style={{ marginBottom: 0 }}>
                                    <div className="class-relative cursor-default listmap-item" style={{ display: 'flex', gap: 5 }}>
                                        <TooltipThumb value={fileName} className="titleDisplay">
                                            <img
                                                style={{ height: 50, width: 70, objectFit: 'cover', cursor: 'pointer' }}
                                                src="/assets/image/others_file.png"
                                                alt=""
                                            />
                                        </TooltipThumb>
                                        <div
                                            style={{
                                                height: 50,
                                                backgroundColor: '#ecedef',
                                                width: 20,
                                                cursor: disabled ? 'default' : 'pointer',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}
                                            onClick={(e) => !disabled && setMenuAnchorEl(e.currentTarget)}
                                        >
                                            <img src="/assets/image/icon-more-panel.svg" alt="" />
                                        </div>
                                        <span
                                            className="file-attachment-thumbnail-label"
                                            style={{
                                                backgroundColor: '#000',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                color: 'white',
                                                borderBottomRightRadius: 5,
                                                fontSize: 11,
                                                textAlign: 'center',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                maxWidth: 60,
                                                minHeight: 14,
                                                minWidth: 33,
                                                zIndex: 1,
                                                pointerEvents: 'none'
                                            }}
                                        >
                                            audio
                                        </span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    )}

                    <Popover
                        open={Boolean(menuAnchorEl)}
                        anchorEl={menuAnchorEl}
                        onClose={() => setMenuAnchorEl(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Button
                                onClick={handleRemoveFile}
                                style={{ fontSize: 15, fontWeight: 400, fontFamily: 'Zen Kaku Gothic New', textTransform: 'none' }}
                            >
                                {trI18n('DELETE')}
                            </Button>
                        </div>
                    </Popover>

                    <div className={`formUpload ${disabled ? 'formUpload-disabled' : ''}`} onDrop={disabled ? undefined : handleDrop} onDragOver={disabled ? undefined : handleDragOver}>
                        <input ref={fileInputRef} type="file" accept=".mp3,.wav" style={{ display: 'none' }} onChange={handleFileInputChange} disabled={disabled} />
                        <label>
                            <Button variant="contained" component="span" onClick={() => fileInputRef.current?.click()} disabled={disabled}>
                                <div className="formUpload-item">
                                    <img src="/assets/image/icon-upload.svg" alt="" onDragStart={e => e.preventDefault()} />
                                    <span className="formUpload-txt">{trI18n('MSG-DRAG-DROP-FILE')}</span>
                                </div>
                            </Button>
                        </label>
                    </div>


                    <ErrorHelper
                        isShowError={error?.holdAudioFile}
                        textError={error?.holdAudioFile}
                        isShowNoticeIcon={false}
                    />
                </div>
            </div>

            {/* Confirm replace dialog */}
            <ConfirmDialog
                isOpen={showReplaceConfirm}
                msgConfirm={trI18n('MSG_HOLD_AUDIO_FILE_REPLACE_CONFIRM')}
                okCallBack={handleConfirmReplace}
                closeCallBack={handleCancelReplace}
                textSubmit={trI18n('COMBTN004')}
                textClose={trI18n('COMBTN001')}
            />
        </>
    );
}

export default HoldAudioFileUpload;

HoldAudioFileUpload.propTypes = {
    projectId: PropTypes.string.isRequired,
    holdMusicFileS3Key: PropTypes.string,
    holdMusicTmpS3Key: PropTypes.string,
    setHoldMusicTmpS3Key: PropTypes.func.isRequired,
    holdMusicRemoved: PropTypes.bool,
    setHoldMusicRemoved: PropTypes.func.isRequired,
    setIsEdited: PropTypes.func.isRequired,
    error: PropTypes.object,
    setError: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};
